import { getJWTToken } from '../../utils/auth';
import { currentSchoolYear } from '../../core/schoolYearsApi';
import type { UploadKind, UploadedFile } from './uploadedFiles';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

type RemoteUpload = {
  id: string;
  name: string;
  kind?: string;
  schoolYear?: string;
  uploadedAt?: string;
};

const SERVER_KIND: Record<UploadKind, string> = {
  wida_access: 'wida',
  alternate_access_wida: 'wida_alt',
  student_roster: 'roster',
};

function toServerKind(kind: UploadKind): string {
  return SERVER_KIND[kind];
}

function toClientKind(kind: string | undefined): UploadKind | undefined {
  if (kind === 'wida') return 'wida_access';
  if (kind === 'wida_alt') return 'alternate_access_wida';
  if (kind === 'roster') return 'student_roster';
  return undefined;
}

export { currentSchoolYear };

export function newestSchoolYearWithData<T extends { kind?: UploadKind; schoolYear?: string }>(
  files: T[],
  current: string = currentSchoolYear(),
): string {
  let newest: string | null = null;
  for (const f of files) {
    if (f.kind === undefined || f.schoolYear == null || f.schoolYear > current) continue;
    if (newest === null || f.schoolYear > newest) newest = f.schoolYear;
  }
  return newest ?? current;
}

export async function fetchUploads(): Promise<UploadedFile[]> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/uploads`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`fetch-uploads failed: ${response.status}`);
  }
  const items = (await response.json()) as RemoteUpload[];
  return items.map((item) => ({
    id: item.id,
    name: item.name,
    kind: toClientKind(item.kind),
    schoolYear: item.schoolYear,
    uploadedAt: item.uploadedAt,
  }));
}

export type UploadContent = {
  id: string;
  name: string;
  kind?: UploadKind;
  csv: string;
};

export async function fetchUploadCsv(id: string): Promise<UploadContent> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/uploads/${id}`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`fetch-upload-csv failed: ${response.status}`);
  }
  const item = (await response.json()) as RemoteUpload & { csv: string };
  return { id: item.id, name: item.name, kind: toClientKind(item.kind), csv: item.csv };
}

export async function updateUploadKind(id: string, kind: UploadKind): Promise<void> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/uploads/${id}`, {
    method: 'PATCH',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ kind: toServerKind(kind) }),
  });
  if (!response.ok) {
    throw new Error(`update-upload-kind failed: ${response.status}`);
  }
}

export type SaveUploadOptions = {
  kind?: UploadKind;
  source?: string;
  page?: string;
  schoolYear?: string;
};

export async function saveUpload(file: File, opts: SaveUploadOptions = {}): Promise<UploadedFile> {
  const { kind, source, page, schoolYear } = opts;
  const token = await getJWTToken();
  const csv = await file.text();
  const response = await fetch(`${apiUrl}/uploads`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...(schoolYear === undefined ? {} : { schoolYear }),
      kind: kind ? toServerKind(kind) : '',
      source: source ?? '',
      page: page ?? '',
      filename: file.name,
      csv,
    }),
  });
  if (!response.ok) {
    throw new Error(`save-upload failed: ${response.status}`);
  }
  const saved = (await response.json()) as RemoteUpload;
  return { id: saved.id, name: file.name, kind: opts.kind, schoolYear: saved.schoolYear ?? schoolYear };
}

export async function deleteUpload(id: string): Promise<void> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/uploads/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!response.ok && response.status !== 404) {
    throw new Error(`delete-upload failed: ${response.status}`);
  }
}
